/**
 * Search Result Item
 *
 * The search flavour of {@link EntityResultRow}: location inline beside the
 * title, matched-terms snippet under it, relative time trailing. Geometry and
 * breadcrumb formatting live in the shared row — this file owns only what is
 * specific to a search hit, which is the snippet parsing.
 *
 * The breadcrumb used to be `metadata.projectName`: the immediate parent's
 * name, resolved for artifacts only. It is now the full ancestor chain from
 * `result.location`, and collections carry one too.
 */

import type { ReactNode } from 'react';
import { HStack, Text, VisuallyHidden } from '@chakra-ui/react';
import { EntityResultRow } from './EntityResultRow';
import type { SearchResult } from '../../../services/search.service';
import { SEARCH_MATCH_TIER } from '@bower/shared/constants/searchMatch';
import { stripMarkdownFragment } from '@bower/shared/utils/strip-markdown';
import { highlightRanges } from './paletteSections';
import {
  getEntityDisplayName,
  type EntityType as ThemeEntityType,
} from '../../../theme/entityIcons';
import { getDisplayTitle } from '../../../utils/artifactUtils';
import { formatTimeAgoShort } from '../../../utils/timeUtils';
import { searchGroupContext, searchGroupMore } from '../../../utils/searchResultGroup';

interface SearchResultItemProps {
  result: SearchResult;
  onSelect: (result: SearchResult) => void;
  showSnippet?: boolean;
  /** The query, to emphasise the words a NAME match matched. */
  query?: string;
}

/**
 * Parse ts_headline output with «» delimiters into React nodes with bold spans.
 * e.g. "some text «matched» more text" → ["some text ", <b>matched</b>, " more text"]
 */
function renderSnippet(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const startIdx = remaining.indexOf('«');
    if (startIdx === -1) {
      nodes.push(remaining);
      break;
    }
    if (startIdx > 0) {
      nodes.push(remaining.substring(0, startIdx));
    }
    const endIdx = remaining.indexOf('»', startIdx);
    if (endIdx === -1) {
      nodes.push(remaining.substring(startIdx));
      break;
    }
    nodes.push(
      <Text as="span" fontWeight="semibold" color="text.secondary" key={key++}>
        {remaining.substring(startIdx + 1, endIdx)}
      </Text>,
    );
    remaining = remaining.substring(endIdx + 1);
  }
  return nodes;
}

/** The title with each matched word in bold, one step above the semibold title. */
function renderTitle(title: string, query: string): ReactNode {
  const ranges = highlightRanges(title, query);
  if (ranges.length === 0) return title;
  const nodes: ReactNode[] = [];
  let at = 0;
  ranges.forEach(([start, end], i) => {
    if (start > at) nodes.push(title.slice(at, start));
    nodes.push(
      <Text as="span" fontWeight="bold" key={i}>
        {title.slice(start, end)}
      </Text>,
    );
    at = end;
  });
  if (at < title.length) nodes.push(title.slice(at));
  return nodes;
}

export function SearchResultItem({ result, onSelect, showSnippet, query }: SearchResultItemProps) {
  // A name match is explained by its title, so the body excerpt would only
  // add an unrelated line (the "| 1 |" row of a handbook). A body or meaning
  // match is explained by its excerpt, so that is when it shows.
  const matchedName = result.match?.field === 'name';
  const snippet = showSnippet && !matchedName ? result.snippet : undefined;
  // A Live run's captures arrive folded into one row: it says which run, and
  // how many more of that run's captures matched.
  const groupContext = searchGroupContext(result);
  const more = searchGroupMore(result);
  const title = getDisplayTitle(result.title);
  // A typo match matched no word as typed, so there is nothing to mark.
  const titleContent =
    query && matchedName && result.match?.tier !== SEARCH_MATCH_TIER.NAME_TYPO
      ? renderTitle(title, query)
      : undefined;

  return (
    <EntityResultRow
      value={result.id}
      onSelect={() => onSelect(result)}
      type={result.type}
      // Spoken type: in a list that mixes every type, the icon says it to
      // sighted users and this says it to everyone else.
      typeLabel={getEntityDisplayName(result.type as ThemeEntityType)}
      title={title}
      titleContent={titleContent}
      location={result.location}
      locationLabel={groupContext}
      placement="inline"
      snippet={snippet ? renderSnippet(stripMarkdownFragment(snippet)) : undefined}
      // Mono, like the eyebrow above it and the keycaps below: a row of ages is
      // a column of numbers, and a proportional face makes "1d" and "11mo" fail
      // to line up down the list.
      trailing={
        result.updatedAt || more > 0 ? (
          <HStack spacing={2} flexShrink={0}>
            {more > 0 && (
              <Text fontFamily="mono" fontSize="11px" color="text.secondary">
                +{more}
                <VisuallyHidden>
                  {` more from this ${result.group?.kind === 'run' ? 'run' : 'session'}`}
                </VisuallyHidden>
              </Text>
            )}
            {result.updatedAt && (
              <Text fontFamily="mono" fontSize="11px" color="text.muted">
                {formatTimeAgoShort(result.updatedAt)}
              </Text>
            )}
          </HStack>
        ) : undefined
      }
    />
  );
}
